import React, { useState, useCallback, useMemo } from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';

import { useAuth } from '../../context/AuthContext';
import { parseTableParams } from '../../utils/Utils';
import DataTable from '../../components/ui/DataTable';
import GenericFormDrawer from '../../components/GenericFormDrawer';
import ConfirmDialog from '../../components/ui/ConfirmDialog';
import OptionsMenu from '../../components/ui/OptionsMenu';
import Button from '../../components/ui/Button';
import LoadErrorBanner from '../../components/ui/LoadErrorBanner';
import useAtsPipelineStagesApi, { PROTECTED_TERMINAL_TYPES } from './config/pipelineStages';

// FE-10 — Süreç Aşamaları. İki kapsamda çalışır:
//   jobPostingId yok  → kiracının VARSAYILAN aşama listesi (her ilan bunu kullanır)
//   jobPostingId var  → o ilana ÖZEL liste (varsa kiracı varsayılanlarını tamamen ezer)
//
// İki çıkmazın ekrandaki karşılığı (sunucu kapıları AtsPipelineStageDataAccess'te):
//   1. Kiracıda hiç aşama yoksa her başvuru `noStagesConfigured` ile düşer → ekran
//      "Varsayılanları Kur" düğmesini gösterir.
//   2. İlana özel listeye TEK satırla başlanamaz (ilanın tüm başvuruları hattın dışına
//      düşerdi) → ekran önce "İlana Özel Liste Oluştur" (kopyalama) sunar, geri dönüş
//      de tek düğme ("Varsayılanlara Dön").

const Hint = styled.p`
  font-size: 12px;
  color: #6c757d;
  margin: 0 0 12px;
`;

const EmptyCard = styled.div`
  background: #fff;
  border: 1px solid #E8ECF0;
  border-radius: 6px;
  padding: 24px;
  margin-bottom: 16px;
`;

const EmptyTitle = styled.h3`
  font-family: 'Nunito Sans', sans-serif;
  font-size: 15px;
  font-weight: 700;
  color: ${({ theme }) => theme.colors.text.primary};
  margin: 0 0 8px;
`;

const EmptyText = styled.p`
  font-family: 'Nunito Sans', sans-serif;
  font-size: 13px;
  color: #6B7280;
  margin: 0 0 16px;
  line-height: 1.5;
`;

export default function PipelineStagesTab({ jobPostingId = null, postingTitle = '' }) {
  const { t } = useTranslation();
  const { canWrite } = useAuth();
  const canManage = canWrite ? canWrite('AT') : false;

  const [queryParams, setQueryParams] = useState({ pageSize: 20, pageNumber: 1 });
  const [filterList, setFilterList] = useState([]);
  const [drawer, setDrawer] = useState({ open: false, row: null });
  const [deleteConfirm, setDeleteConfirm] = useState({ isOpen: false, row: null });
  const [revertConfirm, setRevertConfirm] = useState(false);
  const [busy, setBusy] = useState(false);

  const {
    fetching, loadFailed, data, pagination, columns: baseColumns, objectSchema, typeLabel,
    create, update, remove, restoreDefaults, cloneToPosting, revertPosting,
  } = useAtsPipelineStagesApi(queryParams, jobPostingId);

  const handleDataStateChange = useCallback((action, tableState) => {
    setFilterList(tableState.filterList);
    setQueryParams(parseTableParams(tableState, baseColumns));
  }, [baseColumns]);

  const handleFilterConfirm = useCallback((newFilterList) => setFilterList(newFilterList), []);

  const openNew = useCallback(() => setDrawer({ open: true, row: null }), []);
  const openEdit = useCallback((row) => setDrawer({ open: true, row }), []);

  const actionOptions = useMemo(() => {
    if (!canManage) return [];
    return [
      { type: 'edit', label: t('common.edit'), onClick: openEdit },
      { type: 'divider' },
      {
        type: 'delete',
        label: t('common.delete'),
        onClick: (row) => setDeleteConfirm({ isOpen: true, row }),
      },
    ];
  }, [canManage, t, openEdit]);

  const columns = useMemo(() => {
    if (actionOptions.length === 0) return baseColumns;
    return [
      ...baseColumns,
      {
        name: 'actions',
        label: ' ',
        options: {
          filter: false, sort: false, download: false, print: false,
          customBodyRender: (_, tableMeta) => {
            const row = data[tableMeta.rowIndex];
            if (!row) return null;
            return <OptionsMenu actions={actionOptions} row={row} />;
          },
        },
      },
    ];
  }, [baseColumns, actionOptions, data]);

  const initialValues = useMemo(() => {
    if (!drawer.row) return null;
    return {
      name: drawer.row.name,
      type: drawer.row.type,
      orderIndex: drawer.row.orderIndex,
      defaultSlaHours: drawer.row.defaultSlaHours,
      isTerminal: !!drawer.row.isTerminal,
      color: drawer.row.color || '',
      slug: drawer.row.slug || '',
    };
  }, [drawer.row]);

  const handleSave = useCallback(async (values) => {
    const ok = drawer.row
      ? await update(drawer.row.id, values)
      : await create(values);
    if (ok) setDrawer({ open: false, row: null });
    return ok;
  }, [drawer.row, update, create]);

  const confirmDelete = useCallback(async () => {
    if (!deleteConfirm.row) return;
    setBusy(true);
    await remove(deleteConfirm.row.id);
    setBusy(false);
    setDeleteConfirm({ isOpen: false, row: null });
  }, [remove, deleteConfirm.row]);

  const runRestoreDefaults = useCallback(async () => {
    setBusy(true);
    await restoreDefaults();
    setBusy(false);
  }, [restoreDefaults]);

  const runClone = useCallback(async () => {
    setBusy(true);
    await cloneToPosting();
    setBusy(false);
  }, [cloneToPosting]);

  const confirmRevert = useCallback(async () => {
    setBusy(true);
    await revertPosting();
    setBusy(false);
    setRevertConfirm(false);
  }, [revertPosting]);

  // Boş liste = kurulum gerekiyor. Süzgeç uygulanmışken "boş" boş değildir; o yüzden
  // kurulum kartı yalnız süzgeçsiz VE okuma başarılıyken çıkar.
  const unfiltered = filterList.every((f) => !f || f.length === 0);
  const needsSetup = !fetching && !loadFailed && unfiltered && data.length === 0;

  // Silme kapısı SUNUCUDA (409 ats.pipelineStage.inUse). Ekran o hatayı beklemeden
  // aynı sayıyı gösterir ve diyaloğu TEK DÜĞMELİ bilgilendirmeye çevirir: kullanıcı
  // "Onayla"ya basıp hiçbir şeyin olmamasını izlemesin, ne yapması gerektiğini okusun.
  const deleteBlocked = (deleteConfirm.row?.openApplicationCount || 0) > 0;
  const deleteMessage = deleteConfirm.row
    ? (deleteBlocked
      ? t('ats.pipelineStage.deleteBlockedMessage', {
        name: deleteConfirm.row.name,
        count: deleteConfirm.row.openApplicationCount,
      })
      : t('ats.pipelineStage.deleteConfirm', { name: deleteConfirm.row.name }))
    : '';

  const toolbarActions = useMemo(() => {
    if (!canManage || !jobPostingId || needsSetup) return null;
    return (
      <Button type="button" variant="outline" onClick={() => setRevertConfirm(true)}>
        {t('ats.pipelineStage.revertToDefaults')}
      </Button>
    );
  }, [canManage, jobPostingId, needsSetup, t]);

  return (
    <div>
      <Hint>
        {jobPostingId
          ? t('ats.pipelineStage.postingScopeHint', { title: postingTitle || '' })
          : t('ats.pipelineStage.tenantScopeHint')}
      </Hint>

      {loadFailed && <LoadErrorBanner message={t('ats.pipelineStage.loadFailed')} />}

      {needsSetup && (
        <EmptyCard>
          <EmptyTitle>
            {jobPostingId ? t('ats.pipelineStage.postingEmptyTitle') : t('ats.pipelineStage.tenantEmptyTitle')}
          </EmptyTitle>
          <EmptyText>
            {jobPostingId ? t('ats.pipelineStage.postingEmptyText') : t('ats.pipelineStage.tenantEmptyText')}
          </EmptyText>
          {canManage && (
            <Button
              type="button"
              disabled={busy}
              onClick={jobPostingId ? runClone : runRestoreDefaults}
            >
              {jobPostingId ? t('ats.pipelineStage.cloneDefaults') : t('ats.pipelineStage.restoreDefaults')}
            </Button>
          )}
        </EmptyCard>
      )}

      <GenericFormDrawer
        open={drawer.open}
        onClose={() => setDrawer({ open: false, row: null })}
        title={drawer.row ? t('ats.pipelineStage.edit') : t('ats.pipelineStage.new')}
        schema={objectSchema}
        initialValues={initialValues}
        onSave={handleSave}
      />

      <ConfirmDialog
        isOpen={deleteConfirm.isOpen}
        title={deleteBlocked ? t('ats.pipelineStage.deleteBlockedTitle') : t('common.delete')}
        message={deleteMessage}
        hideCancel={deleteBlocked}
        confirmText={deleteBlocked ? t('ats.pipelineStage.understood') : undefined}
        type={deleteBlocked ? 'primary' : 'danger'}
        onConfirm={deleteBlocked
          ? () => setDeleteConfirm({ isOpen: false, row: null })
          : confirmDelete}
        onCancel={() => setDeleteConfirm({ isOpen: false, row: null })}
        loading={busy}
      />

      <ConfirmDialog
        isOpen={revertConfirm}
        title={t('ats.pipelineStage.revertToDefaults')}
        message={t('ats.pipelineStage.revertConfirm', { title: postingTitle || '' })}
        onConfirm={confirmRevert}
        onCancel={() => setRevertConfirm(false)}
        loading={busy}
        type="primary"
      />

      <DataTable
        title={jobPostingId
          ? t('ats.pipelineStage.postingTitleScoped', { title: postingTitle || '' })
          : t('ats.pipelineStage.title')}
        fetching={fetching}
        columns={columns}
        data={data}
        totalRowCount={pagination.totalRowCount}
        pageIndex={pagination.pageIndex}
        pageSize={pagination.pageSize}
        canCreateNewRecord={canManage && !needsSetup}
        newButtonLabel={t('ats.pipelineStage.new')}
        toolbarActions={toolbarActions}
        onNewButtonClick={openNew}
        onRowClick={canManage ? openEdit : undefined}
        filterList={filterList}
        onFilterConfirm={handleFilterConfirm}
        onTableChanged={handleDataStateChange}
      />

      <Hint>
        {t('ats.pipelineStage.terminalTypesHint', {
          types: PROTECTED_TERMINAL_TYPES.map((x) => typeLabel(x)).join(', '),
        })}
      </Hint>
    </div>
  );
}
